import * as React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { AIDeleteModal } from './AIDeleteModal';
import {
    actionCreators as aiActionCreators
} from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import { useLocalizer } from './hooks/useLocalizer';
import { useAiActionTarget } from './hooks/useAiActionTarget';
import { hidePopoverAndBlur, useBootstrapPopover } from './hooks/useBootstrapPopover';
import { showBootstrapModal } from './actionButtonUtils';
import { DataCollectionAiAgentOperationType } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';

interface AIDeleteAllButtonProps {
    onError?: (message: string) => void;
    localizer?: IStringLocalizer;
    onFilesCleared?: () => void;
    disabled?: boolean;
    operationType?: DataCollectionAiAgentOperationType | null;
}

export const AIDeleteAllButton: React.FC<AIDeleteAllButtonProps> = ({
    onError,
    localizer,
    onFilesCleared,
    disabled = false,
    operationType
}) => {
    const dispatch = useDispatch();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const localize = useLocalizer(localizer);
    const { requestId, operationId } = useAiActionTarget(operationType);
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);
    const isMountedRef = React.useRef(true);

    useBootstrapPopover(buttonRef, disabled);

    React.useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const safeSetDeleteModalOpen = (isOpen: boolean) => {
        if (isMountedRef.current) {
            setIsDeleteModalOpen(isOpen);
        }
    };

    const startDeleteAllAiAnswersFlow = async (): Promise<boolean> => {
        try {
            if (!requestId || requestId.trim() === '') {
                onError?.(localize('Invalid request. Please refresh the page and try again.'));
                return false;
            }

            if (!operationId || operationId.trim() === '') {
                onError?.(localize('No AI operation found to delete. Please refresh the page and try again.'));
                return false;
            }

            const result = await dispatch(
                aiActionCreators.startDeleteAllAiAnswersFlow(requestId, operationId) as any
            );

            if (!result?.success) {
                onError?.(localize('Failed to start delete-all workflow. Please try again.'));
                return false;
            }

            onFilesCleared?.();
            return true;
        } catch {
            onError?.(localize('Failed to start delete-all workflow. Please try again.'));
            return false;
        }
    };

    const showDeleteModal = () => {
        if (disabled) {
            return;
        }
        hidePopoverAndBlur(buttonRef);
        safeSetDeleteModalOpen(true);
        showBootstrapModal('ai-delete-modal');
    };

    const handleDeleteConfirm = async () => {
        const started = await startDeleteAllAiAnswersFlow();
        if (started) {
            safeSetDeleteModalOpen(false);
        }
    };

    const handleDeleteCancel = () => {
        safeSetDeleteModalOpen(false);
    };

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={showDeleteModal}
                disabled={disabled}
                aria-disabled={disabled}
                className={`btn ai-delete-all-button${disabled ? ' disabled' : ''}`}
                data-toggle={disabled ? undefined : 'popover'}
                data-container={disabled ? undefined : 'body'}
                data-placement={disabled ? undefined : 'bottom'}
                data-content={disabled ? undefined : localize('Delete All AI suggestions in the survey, this cannot be undone')}
            >
                <i className="glyphicon glyphicon-trash"></i>
                {localize('Delete All AI Answers')}
            </button>
            <AIDeleteModal
                isOpen={isDeleteModalOpen}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                localizer={localizer}
            />
        </>
    );
};

export default AIDeleteAllButton;
